import { useEffect, useState } from 'react';
import { api } from '../api';
import { toast } from 'sonner';
import { MapPin, Clock, CheckCircle } from 'lucide-react';

const STATUS_COLOR = {
  assigned:  'bg-blue-50 border-blue-200',
  en_route:  'bg-purple-50 border-purple-200',
  on_site:   'bg-indigo-50 border-indigo-200',
  completed: 'bg-green-50 border-green-200',
  cancelled: 'bg-gray-50 border-gray-200',
};
const STATUS_BADGE = {
  assigned:'bg-blue-100 text-blue-700', en_route:'bg-purple-100 text-purple-700',
  on_site:'bg-indigo-100 text-indigo-700', completed:'bg-green-100 text-green-700',
  cancelled:'bg-gray-100 text-gray-500',
};
const NEXT_ACTIONS = {
  assigned:  { label: '🚗 I\'m on my way',  next: 'en_route' },
  en_route:  { label: '📍 I\'ve arrived',   next: 'on_site' },
  on_site:   { label: '✅ Job complete',     next: 'completed' },
};

export default function WorkerView() {
  const [jobs, setJobs]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote]     = useState('');
  const [activeNote, setActiveNote] = useState(null);

  useEffect(() => { load(); }, []); // eslint-disable-line
  const load = () => {
    api.get('/opsive/jobs')
      .then(data => { setJobs(data); setLoading(false); })
      .catch(e => { toast.error(e.message); setLoading(false); });
  };

  const advance = async (job_id, status) => {
    try {
      await api.patch(`/opsive/jobs/${job_id}`, { status });
      toast.success(`Updated: ${status.replace('_', ' ')}`);
      load();
    } catch(e) { toast.error(e.message); }
  };

  const addNote = async (job_id) => {
    if (!note.trim()) return;
    await api.patch(`/opsive/jobs/${job_id}`, { note });
    setNote(''); setActiveNote(null);
    toast.success('Note saved');
    load();
  };

  const active = jobs.filter(j => j.status !== 'completed' && j.status !== 'cancelled');
  const done   = jobs.filter(j => j.status === 'completed' || j.status === 'cancelled');

  if (loading) return <div className="text-center py-16 text-gray-400">Loading your jobs…</div>;

  return (
    <div className="max-w-lg mx-auto space-y-4">
      <h1 className="text-xl font-bold text-gray-900">My Jobs</h1>

      {active.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <CheckCircle className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No active jobs assigned to you right now.</p>
        </div>
      )}

      {active.map(job => {
        const action = NEXT_ACTIONS[job.status];
        return (
          <div key={job.job_id} className={`border rounded-2xl overflow-hidden ${STATUS_COLOR[job.status] || 'bg-white border-gray-200'}`}>
            <div className="px-5 py-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_BADGE[job.status]}`}>
                    {job.status.replace('_',' ')}
                  </span>
                  <h3 className="font-bold text-gray-900 mt-1">{job.title}</h3>
                  {job.client_name && <p className="text-sm text-gray-600">{job.client_name}</p>}
                </div>
                <span className="text-xs text-gray-400 capitalize bg-white px-2 py-1 rounded-full border border-gray-100">
                  {job.priority}
                </span>
              </div>

              {job.address && (
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(job.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-sm text-blue-600 mt-2 hover:underline"
                >
                  <MapPin className="w-3.5 h-3.5" /> {job.address}
                </a>
              )}

              {job.scheduled_at && (
                <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1">
                  <Clock className="w-3.5 h-3.5" />
                  {new Date(job.scheduled_at).toLocaleString()}
                </div>
              )}

              {job.description && (
                <p className="text-sm text-gray-600 mt-2">{job.description}</p>
              )}
            </div>

            <div className="px-5 pb-4 flex flex-col gap-2">
              {action && (
                <button
                  onClick={() => advance(job.job_id, action.next)}
                  className="w-full py-2.5 bg-gray-900 text-white font-semibold text-sm rounded-xl hover:bg-gray-700 transition-colors"
                >
                  {action.label}
                </button>
              )}
              {activeNote === job.job_id ? (
                <div className="flex gap-2">
                  <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note…"
                    className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400" />
                  <button onClick={() => addNote(job.job_id)} className="text-sm px-3 py-2 bg-blue-600 text-white rounded-lg">Save</button>
                  <button onClick={() => setActiveNote(null)} className="text-sm px-3 py-2 border border-gray-200 rounded-lg text-gray-500">✕</button>
                </div>
              ) : (
                <button onClick={() => setActiveNote(job.job_id)}
                  className="text-xs text-gray-500 hover:text-gray-700 text-left">
                  + Add note
                </button>
              )}
            </div>
          </div>
        );
      })}

      {done.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Completed / Cancelled</p>
          <div className="space-y-2">
            {done.map(job => (
              <div key={job.job_id} className="bg-white border border-gray-100 rounded-xl px-5 py-3 flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-700 text-sm">{job.title}</p>
                  <p className="text-xs text-gray-400">{job.client_name}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[job.status]}`}>
                  {job.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
